import { LoaderFunctionArgs, ActionFunctionArgs } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { QueryClient } from '@tanstack/react-query';
import { MemberRepository } from 'src/api/member_repository';
import { DEFAULT_PAGE_SIZE } from 'src/components/pagination/use_pagination';
import { InvitationsError } from 'src/types/errors';
import { MemberAction, MemberActionResult } from 'src/types/loaders';
import { InvitationInput } from 'src/types/member_management';

export const membersLoader =
  (queryClient: QueryClient, memberRepository: MemberRepository) =>
  async ({ request, params }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const searchParams = url.searchParams;

    const queryParams = {
      page: parseInt(searchParams.get('page') || '1'),
      perPage: parseInt(
        searchParams.get('perPage') || DEFAULT_PAGE_SIZE.toString(),
      ),
      organizationName: params.organization,
    };

    const queryKey = ['members', queryParams];

    // Load members
    await queryClient.ensureQueryData({
      queryKey,
      queryFn: async () => {
        return memberRepository.getMembers(queryParams);
      },
    });

    // Load assignable roles
    await queryClient.ensureQueryData({
      queryKey: ['assignableRoles'],
      queryFn: async () => {
        return memberRepository.getAssignableRoles();
      },
    });

    // Return members query key
    return {
      queryKey,
    };
  };

export const invitesLoader =
  (queryClient: QueryClient, memberRepository: MemberRepository) =>
  async ({ request, params }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const searchParams = url.searchParams;

    const queryParams = {
      page: parseInt(searchParams.get('page') || '1'),
      perPage: parseInt(
        searchParams.get('perPage') || DEFAULT_PAGE_SIZE.toString(),
      ),
      organizationName: params.organization,
    };

    const queryKey = ['invites', queryParams];

    await queryClient.ensureQueryData({
      queryKey,
      queryFn: async () => {
        return memberRepository.getInvites(queryParams);
      },
    });

    return {
      queryKey,
    };
  };

export const memberAction = (
  queryClient: QueryClient,
  memberRepository: MemberRepository,
) => {
  return async ({
    request,
    params,
  }: ActionFunctionArgs): Promise<MemberActionResult> => {
    const organizationName = params.organization as string;
    const formData = await request.formData();
    const action = formData.get('actionType') as MemberAction;

    try {
      switch (action) {
        case MemberAction.INVITE_USERS: {
          const invites = JSON.parse(
            formData.get('invites')?.toString() || '[]',
          ) as InvitationInput[];

          try {
            await memberRepository.inviteUsers(invites, organizationName);
            queryClient.invalidateQueries({ queryKey: ['invites'] });

            return {
              success: true,
              action: MemberAction.INVITE_USERS,
            };
          } catch (error) {
            if (error instanceof InvitationsError) {
              queryClient.invalidateQueries({ queryKey: ['invites'] });

              return {
                success: false,
                action: MemberAction.INVITE_USERS,
                invitationErrors: error.errors,
                successfulInvitations: error.successfulInvitations,
              };
            }

            throw error;
          }
        }

        case MemberAction.ASSIGN_ROLE: {
          const memberId = formData.get('memberId') as string;
          const roleIds = formData
            .getAll('roles')
            .map((role) => role.toString());

          const updatedMember = await memberRepository.assignRole(
            memberId,
            roleIds,
            organizationName,
          );

          queryClient.invalidateQueries({ queryKey: ['members'] });
          queryClient.setQueryData(['member', memberId], updatedMember);

          return {
            success: true,
            action: MemberAction.ASSIGN_ROLE,
          };
        }
        case MemberAction.REMOVE_USER: {
          const memberId = formData.get('memberId') as string;

          await memberRepository.removeUser(memberId, organizationName);
          queryClient.invalidateQueries({ queryKey: ['members'] });
          queryClient.removeQueries({ queryKey: ['member', memberId] });

          return {
            success: true,
            action: MemberAction.REMOVE_USER,
          };
        }

        case MemberAction.REVOKE_INVITE: {
          const inviteId = formData.get('inviteId') as string;

          await memberRepository.removeInvite(inviteId, organizationName);
          queryClient.invalidateQueries({ queryKey: ['invites'] });
          queryClient.removeQueries({ queryKey: ['invite', inviteId] });

          return {
            success: true,
            action: MemberAction.REVOKE_INVITE,
          };
        }

        default:
          return {
            success: false,
            action: action as MemberAction,
          };
      }
    } catch (error) {
      if (
        error &&
        typeof error === 'object' &&
        'status' in error &&
        error.status === 302
      ) {
        throw error;
      }

      Sentry.captureException(error, {
        tags: { action: action as MemberAction },
        extra: { message: 'Member action error' },
      });
      return {
        success: false,
        action: action as MemberAction,
      };
    }
  };
};
